import { DataServices } from './DataServices'
import { DataRecord } from './DataRecord'
import { ApiCallResult } from '../types/ApiCallResult'

import LRUCache from 'lru-cache';
import { Participant } from "../Participant/Participant";
import { AuditLog } from "../Participant/AuditLog";
import { DataTools } from './DataTools';
import { LogicEngine } from '../LogicEngine/LogicEngine';

const querystring = require('querystring')

export interface PipelineArguments
{
	option_1?: string;
	option_2?: string;
	field_id?: string;
	participant_id?: string;
	current_user?: string;
	sort?: string;
	limit?: string;
}

/**
 * The primary method for getting single records or a set of records.
 * @category Data and API Services
 */
export class DataQuery
{
	static _instance: DataQuery | undefined = undefined;

	/* short term cache for the query results */
	queryCache: LRUCache<string, DataRecord[]>;

	/* short term cache for the query results */
	countCache: LRUCache<string, number>;

	/**
	 * Return an instance of the class which will give shared cache.
	 */
	static instance(): DataQuery
	{
		if (!DataQuery._instance)
			DataQuery._instance = new DataQuery();
		return DataQuery._instance;
	}

	constructor()
	{
		this.queryCache = new LRUCache<string, DataRecord[]>({
			ttl: 100,
			max: 10,   // max of 10 items in the cache
		});

		this.countCache = new LRUCache<string, number>({
			max: 1000,   // max 1000 count type queries
			ttl: 300,    // keep for 300 ms each
		});
	}

	/** Reset the query cache and count cache */
	static resetCache()
	{
		DataQuery.instance().queryCache = new LRUCache<string, DataRecord[]>({
			max: 10,   // max of 10 items in the cache
			ttl: 50,   // 50 ms
		});

		DataQuery.instance().countCache = new LRUCache<string, number>({
			max: 1000,   // max 1000 count type queries
			ttl: 300,    // keep for 300 ms each
		});
	}

	/** Put debugging code in here */
	logQuery(tableName: string, queryText: string, results: DataRecord[])
	{
		// console.log("Query tableName=", tableName, "Query=", queryText, "Results=", results);
	}

	/** Execute a query where the queryText contains the table name and query
	 *
	 * @example
	 * DataQuery.instance().executeCountQueryByText("Participant;status=C")
	 *
	 * @returns A number that represents the total records
	 */
	async executeCountQueryByText(queryText: string): Promise<number>
	{
		const re = new RegExp("^([a-zA-Z0-9\_]+);(.*)");
		const match = queryText.match(re);
		if (match)
		{
			let results = await this.executeCount(match[1], match[2]);
			return results;
		} else
		{
			// Handle invalid call
			console.error("Invalid call to executeCountQueryByText(", queryText, ")");
		}

		return 0;
	}

	/** Execute a query where the queryText contains the table name and query
	 *
	 * @example
	 * Participant;status=C
	 */
	async executeQueryByText(queryText: string): Promise<DataRecord[]>
	{
		const re = new RegExp("^([#a-zA-Z0-9\_]+);(.*)");
		const match = queryText.match(re);
		if (match)
		{
			let results = await this.executeQuery(match[1], match[2]);
			return results;
		} else
		{
			// Handle invalid call
			console.error("Invalid call to executeQueryByText(", queryText, ")");
		}

		return [];
	}

	/**
	 * Execute a query against local data or server data
	 *
	 * @param id {string}
	 * @param tableName {string}
	 */
	async executeQueryById(tableName: string, id: string): Promise<DataRecord | null>
	{
		/** Special case for loading in memory tables by ID */
		if (tableName.startsWith("#"))
		{
			return DataRecord.loadFromMemoryById(tableName, id);
		}

		// Special case check, if not passing an ID value, we should have an error
		if (DataTools.internalValidateString(id) === "")
		{
			console.error("Error in DataQuery::executeQueryById: Missing the ID value");
			return null;
		}

		const ds = DataServices.instance();
		let apiResult: ApiCallResult<any> = await ds.makeApiCall<any>(`/api/v1/data/${tableName}/get/${id}`, null);
		if (apiResult.isError)
		{
			// TODO Deal with error condition reporting
			console.error("Error in DataQuery:executeQueryById: ", id, "Error=", apiResult.errorMessage);
			return null;
		}

		if (apiResult.result)
		{
			const record = new DataRecord(tableName, apiResult.result);
			return record;
		}

		return null;
	}

	/** Helper function that pulls out the subgroup= text from a query if found. returns '' if not. */
	internalGetSumCommand(queryText: string): string
	{
		const re = new RegExp(/(&{0,1}sumgroup=[^&]+)/i);
		const m = re.exec(DataTools.internalValidateString(queryText));
		if (!m) return "";
		return m[0];
	}

	/**
	 * Call the server Pipeline function.  Executes a query on the server using aggregate and
	 * returns the records here
	 * @param tableName Primary table name for data dictionary
	 * @param pipelineQuery url encoded values to pass to the query
	 * @returns
	 */
	async executePipeline(tableName: string, pipelineQuery: string)
	{
		const cacheKey = "pipeline" + pipelineQuery;
		const cachedValue = this.queryCache.get(cacheKey);
		if (cachedValue) return cachedValue;

		// Force an in memory table
		tableName = "#" + tableName.replace("#", "");

		// PipelineQuery should be a string in URL Encoded format like this
		// Table;filename=<something>&variable=value&variable=value

		const vars = querystring.parse(pipelineQuery);
		const reFilename = DataTools.internalValidateString(vars["filename"] || vars["file"]);
		if (!reFilename || reFilename === "")
		{
			if (LogicEngine.debugOutput)
				console.log("executePipeline Failed to parse query filename:", pipelineQuery);
			return [];
		}

		vars['fileName'] = reFilename;
		vars['current_user'] = LogicEngine.currentUser;
		vars['current_participant'] = LogicEngine.currentParticipantID;

		const ds = DataServices.instance();
		let apiResult: ApiCallResult<any[]> = await ds.makeApiCall<any[]>(`/api/v1/data/${tableName.replace("#", "")}/run-pipeline`, vars, 'post');
		if (apiResult.isError)
		{
			// TODO Deal with error condition reporting
			console.error("Error in DataQuery:executePipeline: ", pipelineQuery, "Error=", apiResult.errorMessage);
			return [];
		}

		const list: DataRecord[] = [];

		if (typeof apiResult.result === "string")
		{
			console.error("executePipeline Server Error:", apiResult.result);
			return [];
		}

		if (apiResult.result)
		{
			for (let element of apiResult.result)
			{
				const record = new DataRecord(tableName, element);
				await record.fixRecord();
				list.push(record);
			}
		}

		this.logQuery(tableName, pipelineQuery, list);
		this.queryCache.set(cacheKey, list);
		return list;
	}

	/**
	 * Execute a query against local data or server data
	 *
	 * @param {string} tableName - table name
	 * @param {string} queryText - query text
	 * @param {boolean} includeParticipant - flag for include participants
	 * @see https://github.com/loris/api-query-params Query string to MongoDB Query Converter
	 */
	async executeQuery(tableName: string, queryText: string, includeParticipant: boolean = false): Promise<DataRecord[]>
	{
		// Check to see if this is a pipeline query
		// if so, it will contain filename=<something> or file=<something>
		const rePipelineTest1 = new RegExp("filename=([a-zA-Z0-9]+)");
		if (rePipelineTest1.test(queryText)) return this.executePipeline(tableName, queryText);
		const rePipelineTest2 = new RegExp("file=([a-zA-Z0-9]+)");
		if (rePipelineTest2.test(queryText)) return this.executePipeline(tableName, queryText);

		const cachedValue = this.queryCache.get(tableName + queryText);
		if (cachedValue)
			return cachedValue;

		/* Special case for temporary in memory tables.  We don't support query without ID */
		if (tableName.startsWith("#"))
		{
			return DataRecord.loadFromMemoryByQuery(tableName, queryText);
		}

		//  Pull out special case for "sum" command
		let sumgroupText = this.internalGetSumCommand(queryText);
		if (sumgroupText.length > 0)
		{
			queryText = queryText.replace(sumgroupText, "");
			sumgroupText = sumgroupText.replace(/sumgroup=/i, "");
			sumgroupText = sumgroupText.replace(/&/g, "");
			console.log("SUMGROUP=", sumgroupText);
		}

		const ds = DataServices.instance();
		let apiResult: ApiCallResult<any[]> = await ds.makeApiCall<any[]>(`/api/v1/data/${tableName}/search?${queryText}`, null);
		if (apiResult.isError)
		{
			// TODO Deal with error condition reporting
			console.error("Error in DataQuery:executeQuery: ", queryText, "Error=", apiResult.errorMessage);
			return [];
		}

		const list: DataRecord[] = [];

		if (apiResult.result)
		{
			for (let element of apiResult.result)
			{
				if (includeParticipant && element.participant_id)
				{
					const participant = new Participant();
					await participant.loadById(element.participant_id)
					if (participant.participantDataRecord && participant.participantDataRecord.data)
					{
						for (const field in participant.participantDataRecord.data)
						{
							element[`p_${field}`] = participant.participantDataRecord.data[field];
						}
					}
				}
				const record = new DataRecord(tableName, element);
				await record.fixRecord();

				list.push(record);
			}

		}

		this.logQuery(tableName, queryText, list);
		this.queryCache.set(tableName + queryText, list);
		return list;
	}

	/**
	 * Execute a count query against local data or server data
	 *
	 * @param tableName The tableName
	 * @param queryText The query string
	 * @see https://github.com/loris/api-query-params Query string to MongoDB Query Converter
	 */
	async executeCount(tableName: string, queryText: string): Promise<number>
	{
		const cachedValue = this.countCache.get(tableName + queryText);
		if (cachedValue)
			return cachedValue;

		if (tableName.startsWith("#"))
		{
			/*  Do not support memory records in a count query */
			return 0;
		}

		const ds = DataServices.instance();
		let apiResult: ApiCallResult<any> = await ds.makeApiCall<any[]>(`/api/v1/data/${tableName}/count?${queryText}`, null);
		if (apiResult.isError)
		{
			// TODO Deal with error condition reporting
			console.error("Error in DataQuery:executeCount: ", queryText, "Error=", apiResult.errorMessage);
			return 0;
		}

		this.countCache.set(tableName + queryText, apiResult.result.count);
		return apiResult.result.count;
	}
}
